import { Component, OnInit, computed, signal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { OrganizationMember } from 'src/app/models/membership.model';
import { Project } from 'src/app/models/project.model';
import { Team } from 'src/app/models/team.model';
import { AuthService } from 'src/app/services/auth.service';
import { MembershipService } from 'src/app/services/membership.service';
import { ProjectManagementService } from 'src/app/services/project-management.service';
import { ToastService } from 'src/app/services/toast.service';

interface Draft {
  name: string;
  members: Set<string>;
  projects: Set<string>;
}

@Component({
  selector: 'app-teams',
  templateUrl: './teams.component.html',
  styleUrls: ['../organization/organization.component.scss', './teams.component.scss'],
  standalone: false,
})
export class TeamsPageComponent implements OnInit {
  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly teams = signal<Team[]>([]);
  readonly members = signal<OrganizationMember[]>([]);
  readonly projects = signal<Project[]>([]);

  readonly role = signal<'owner' | 'admin' | 'member'>(this.auth.orgRole());
  readonly isOwner = computed(() => this.role() === 'owner');
  readonly seesAll = computed(() => this.role() === 'owner' || this.role() === 'admin');

  readonly newTeamName = signal('');
  readonly creating = signal(false);
  readonly createError = signal<string | null>(null);

  readonly editing = signal<string | null>(null);
  readonly draft = signal<Draft | null>(null);
  readonly saving = signal(false);
  readonly confirmingDelete = signal<string | null>(null);

  /** Projects in no team: only the owner and admins can see them. */
  readonly unassignedProjects = computed(() => {
    const assigned = new Set(this.teams().flatMap((t) => t.projects.map((p) => p.uuid)));
    return this.projects().filter((p) => p.uuid && !assigned.has(p.uuid));
  });

  constructor(
    private membership: MembershipService,
    private projectService: ProjectManagementService,
    private toast: ToastService,
    private auth: AuthService,
  ) {}

  async ngOnInit() {
    try {
      this.role.set(await lastValueFrom(this.membership.myRole()));
      const [teams, projects, members] = await Promise.all([
        lastValueFrom(this.membership.getTeams()),
        lastValueFrom(this.projectService.getProjects()),
        this.isOwner() ? lastValueFrom(this.membership.getMembers()) : Promise.resolve([]),
      ]);
      this.teams.set(teams);
      this.projects.set(projects);
      this.members.set(members);
    } catch {
      this.loadError.set(true);
    }
    this.loading.set(false);
  }

  fullName(person: { first_name?: string; last_name?: string; email?: string }): string {
    return `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim() || person.email || '';
  }

  async create() {
    const name = this.newTeamName().trim();
    if (!name || this.creating()) return;
    this.creating.set(true);
    this.createError.set(null);
    try {
      const team = await lastValueFrom(this.membership.createTeam(name));
      this.teams.update((list) => [...list, team].sort((a, b) => a.name.localeCompare(b.name)));
      this.newTeamName.set('');
      this.startEditing(team); // straight on to picking members and projects
    } catch (err: any) {
      this.createError.set(err?.name?.[0] ?? err?.detail ?? 'Could not create the team.');
    } finally {
      this.creating.set(false);
    }
  }

  startEditing(team: Team) {
    this.confirmingDelete.set(null);
    this.editing.set(team.uuid);
    this.draft.set({
      name: team.name,
      members: new Set(team.members.map((m) => m.uuid)),
      projects: new Set(team.projects.map((p) => p.uuid)),
    });
  }

  toggle(kind: 'members' | 'projects', uuid: string) {
    this.draft.update((draft) => {
      if (!draft) return draft;
      const next = new Set(draft[kind]);
      next.has(uuid) ? next.delete(uuid) : next.add(uuid);
      return { ...draft, [kind]: next };
    });
  }

  setDraftName(name: string) {
    this.draft.update((draft) => (draft ? { ...draft, name } : draft));
  }

  async save(team: Team) {
    const draft = this.draft();
    if (!draft || this.saving()) return;
    this.saving.set(true);
    try {
      const updated = await lastValueFrom(
        this.membership.updateTeam(team.uuid, {
          name: draft.name.trim(),
          member_uuids: [...draft.members],
          project_uuids: [...draft.projects],
        }),
      );
      this.teams.update((list) => list.map((t) => (t.uuid === updated.uuid ? updated : t)));
      this.editing.set(null);
      this.toast.show(`${updated.name} saved`, 'success');
    } catch (err: any) {
      this.toast.show(err?.name?.[0] ?? err?.detail ?? 'Could not save the team', 'error');
    } finally {
      this.saving.set(false);
    }
  }

  async remove(team: Team) {
    this.saving.set(true);
    try {
      await lastValueFrom(this.membership.deleteTeam(team.uuid));
      this.teams.update((list) => list.filter((t) => t.uuid !== team.uuid));
      this.toast.show(`${team.name} deleted`, 'success');
    } catch {
      this.toast.show('Could not delete the team', 'error');
    } finally {
      this.saving.set(false);
      this.confirmingDelete.set(null);
    }
  }
}
